/**
 * Custom Image Loader for Next.js
 * Converts absolute domain URLs to relative paths for NGINX proxy.
 * This solves the hairpin NAT issue on AWS where containers
 * cannot reach the instance's own public IP.
 *
 * Flow: Next.js → relative URL → NGINX proxy → CloudFront → S3
 *
 * Benefits:
 * - SEO-friendly URLs (domain.com/uploads/...)
 * - Avoids hairpin NAT issue (no loopback to own public IP)
 * - CloudFront CDN caching via NGINX proxy
 * - Environment-agnostic (uses NEXT_PUBLIC_SITE_URL)
 *
 * ── Responsive WebP variánsok ──────────────────────────────────────────
 * A backend minden képhez pre-generálja (ha a forrás elég nagy):
 *   foto.webp         → full-res (max 2560px)
 *   foto-320w.webp    → 320px széles  (csak ha forrás > 320px)
 *   foto-640w.webp    → 640px széles  (csak ha forrás > 640px)
 *   foto-1080w.webp   → 1080px széles (csak ha forrás > 1080px)
 *   foto-1920w.webp   → 1920px széles (csak ha forrás > 1920px)
 *
 * A backend best_res mezőben tárolja a forrás szélességét (px).
 * A loader ezt figyelembe veszi: csak olyan variánst kér, ami tényleg létezik.
 *
 * Ha az src .webp végű saját uploads URL, a loader a `width` és `bestRes`
 * paraméter alapján a megfelelő (és létező) méretvariáns path-ját adja vissza.
 *
 * Nem .webp src esetén (JPEG/PNG) az eredeti path-t adjuk vissza.
 *
 * best_res átadása: a Next.js Image `loader` prop csak { src, width, quality }
 * paramétert kap. A best_res értéket az src URL query paraméterként kódoljuk:
 *   foto.webp?br=700
 * A loader ezt kinyeri és figyelembe veszi, majd eltávolítja a végső URL-ből.
 */

/** Elérhető előre generált szélességek növekvő sorrendben */
const WEBP_WIDTHS = [320, 640, 1080, 1920];

/**
 * Visszaadja a legnagyobb variáns szélességet amelyik tényleg elkészült.
 * Egy variáns létezik ha: best_res > variáns_szélesség
 *   pl. best_res=700  → 640w ✓, 1080w ✗  → maxAvailable=640
 *       best_res=300  → egyik sem        → maxAvailable=null (csak full-res)
 *       best_res=2000 → mind             → maxAvailable=1920
 *       best_res=null → ismeretlen       → null (nincs clamp, régi viselkedés)
 */
function maxAvailableVariant(bestRes) {
  if (bestRes === null || bestRes === undefined) return undefined; // ismeretlen → régi viselkedés
  const available = WEBP_WIDTHS.filter(w => bestRes > w);
  return available.length > 0 ? available[available.length - 1] : null;
}

/**
 * `width` alapján kiválasztja a legkisebb megfelelő variáns szélességét,
 * figyelembe véve a ténylegesen elérhető maximumot (bestRes alapján).
 *
 * @param {number} width     - A Next.js által kért képszélesség (px)
 * @param {number|null} bestRes - Forrás szélesség px-ben (provider_storage.best_res)
 *                                null = ismeretlen (régi sor, nincs clamp)
 * @returns {number|null}    - Variáns szélesség, vagy null = full-res foto.webp
 */
function pickVariantWidth(width, bestRes) {
  const maxVariant = maxAvailableVariant(bestRes);

  // bestRes ismeretlen (régi sor) → eredeti viselkedés, nincs clamp
  if (maxVariant === undefined) {
    for (const w of WEBP_WIDTHS) {
      if (width <= w) return w;
    }
    return null; // full-res
  }

  // bestRes ismert, de egyik variáns sem készült el (pl. best_res=300)
  if (maxVariant === null) return null; // → full-res foto.webp

  // Legkisebb variáns ami >= a kért szélességnek, de <= maxVariant
  for (const w of WEBP_WIDTHS) {
    if (w > maxVariant) break;       // ennél nagyobb variáns nincs
    if (width <= w) return w;        // ez a legkisebb megfelelő
  }
  // width > maxVariant → full-res foto.webp (ez a legnagyobb elérhető, nagyobb mint bármely variáns)
  return null;
}

export default function customImageLoader({ src, width, quality }) {
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;

  // best_res kinyerése az src query paraméterből (?br=N)
  // A SafeImage/hívó hely így adja át: `${webpUrl}?br=${best_res}`
  let bestRes = null;
  let hasBestResMeta = false;
  let cleanSrc = src;
  const brMatch = src.match(/[?&]br=(\d+)(?:&|$)/);
  if (brMatch) {
    hasBestResMeta = true;
    bestRes = parseInt(brMatch[1], 10);
    // Eltávolítjuk a ?br=N paramétert a végső URL-ből
    cleanSrc = src.replace(/([?&])br=\d+(&|$)/, (_, pre, post) =>
      pre === '?' && post === '' ? '' :
      pre === '?' && post === '&' ? '?' :
      post === '' ? '' : pre
    ).replace(/[?&]$/, '');
  }

  // Saját domain-t azonosítja – ismert prod/test domain VAGY bármely host /uploads/ path-ja
  const knownDomains = [
    siteUrl,
    'https://eskuvo2.helyszin.com',
    'https://test.eskuvohelyszin.brighthills.cloud',
  ].filter(Boolean);

  const isKnownDomain = knownDomains.some(d => cleanSrc.startsWith(`${d}/uploads/`));

  // Bármely host /uploads/ útvonalon – dev-en pl. http://192.168.0.24:2000/uploads/
  const isUploadsPath = /^https?:\/\/[^/]+\/uploads\//.test(cleanSrc);

  if (isKnownDomain || isUploadsPath) {
    // Ismert domain esetén: abszolút → relatív path (hairpin NAT megkerülés)
    // Ismeretlen host esetén (pl. dev 192.168.x.x): az URL prefix marad, csak a fájlnév cserélődik
    let relativeSrc = cleanSrc;
    const matchedDomain = knownDomains.find(d => cleanSrc.startsWith(`${d}/`));
    if (matchedDomain) {
      relativeSrc = cleanSrc.replace(matchedDomain, '');
    }

    // Ha WebP, csak explicit best_res meta esetén kérjünk méretvariánst.
    // Meta nélkül maradjon a full-res path, így nem kérünk nem garantált variánst.
    if (relativeSrc.endsWith('.webp')) {
      if (!hasBestResMeta) {
        return relativeSrc;
      }
      const variantW = pickVariantWidth(width, bestRes);
      if (variantW !== null) {
        // foto.webp → foto-640w.webp (vagy a legjobb elérhető variáns)
        return relativeSrc.replace(/\.webp$/, `-${variantW}w.webp`);
      }
      // variantW === null → nincs variáns (best_res <= 640) vagy width > 1920 → full-res
      return relativeSrc;
    }

    // Nem-WebP saját kép (JPEG/PNG) – path változatlanul
    return relativeSrc;
  }

  // Külső URL-ek változatlanul
  return cleanSrc;
}
